import React, { useState, useEffect } from 'react';
import { X, Plus, Trash2, ChevronDown, ChevronUp } from 'lucide-react';

interface Feature {
  id: number;
  name: string;
}

interface Plan {
  id?: number;
  plan_name: string;
  description: string;
  monthly_price: any;
  yearly_price: any;
  features: Feature[];
  is_active: boolean;
  is_featured: boolean;
  memory_limit: any;
  memory_unit: string;
  recipient_limit: any;
}

interface PlanFormModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (planData: Partial<Plan>) => void;
  initialData?: Plan;
  isEditMode?: boolean;
}

const PlanFormModal: React.FC<PlanFormModalProps> = ({
  isOpen,
  onClose,
  onSubmit,
  initialData,
  isEditMode = false
}) => {
  const [formData, setFormData] = useState<Partial<Plan>>({
    plan_name: '',
    description: '',
    monthly_price: 0,
    yearly_price: 0,
    features: [],
    is_active: true,
    is_featured: false,
    memory_limit: 1,
    memory_unit: 'GB',
    recipient_limit: 100
  });
  
  const [newFeature, setNewFeature] = useState<string>('');
  const [formErrors, setFormErrors] = useState<{ [key: string]: string }>({});
  const [showFeatures, setShowFeatures] = useState<boolean>(true);
  
  // Initialize form with initial data if provided
  useEffect(() => {
    if (initialData) {
      setFormData(initialData);
    }
  }, [initialData]);
  
  if (!isOpen) return null;
  
  // Handle input change
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target as HTMLInputElement;
    
    if (type === 'checkbox') {
      const checked = (e.target as HTMLInputElement).checked;
      setFormData({ ...formData, [name]: checked });
    } else if (type === 'number') {
      setFormData({ ...formData, [name]: parseFloat(value) });
    } else {
      setFormData({ ...formData, [name]: value });
    }
    
    // Clear error for this field
    if (formErrors[name]) {
      const newErrors = { ...formErrors };
      delete newErrors[name];
      setFormErrors(newErrors);
    }
  };
  
  // Add new feature
  const addFeature = () => {
    if (!newFeature.trim()) return;
    
    const newFeatureObj: Feature = {
      id: Date.now(), // Temporary ID, will be replaced by server
      name: newFeature.trim()
    };
    
    setFormData({
      ...formData,
      features: [...(formData.features || []), newFeatureObj]
    });
    
    setNewFeature('');
  };
  
  // Remove feature
  const removeFeature = (id: number) => {
    setFormData({
      ...formData,
      features: (formData.features || []).filter(feature => feature.id !== id)
    });
  };
  
  // Validate form before submit
  const validateForm = (): boolean => {
    const errors: { [key: string]: string } = {};
    
    if (!formData.plan_name?.trim()) {
      errors.plan_name = 'Plan name is required';
    }
    
    if (!formData.description?.trim()) {
      errors.description = 'Description is required';
    }
    
    if (formData.monthly_price === undefined || formData.monthly_price < 0) {
      errors.monthly_price = 'Monthly price must be a positive number';
    }
    
    if (formData.yearly_price === undefined || formData.yearly_price < 0) {
      errors.yearly_price = 'Yearly price must be a positive number';
    }
    
    if (formData.memory_limit === undefined || formData.memory_limit <= 0) {
      errors.memory_limit = 'Memory limit must be greater than 0';
    }
    
    if (!formData.memory_unit) {
      errors.memory_unit = 'Memory unit is required';
    }
    
    if (formData.recipient_limit === undefined || formData.recipient_limit <= 0) {
      errors.recipient_limit = 'Media limit must be greater than 0';
    }
    
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };
  
  // Handle form submission
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    if (validateForm()) {
      onSubmit(formData);
    }
  };
  
  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
        <div className="fixed inset-0 transition-opacity" onClick={onClose}>
          <div className="absolute inset-0 bg-gray-500 opacity-75"></div>
        </div>

        <span className="hidden sm:inline-block sm:align-middle sm:h-screen">&#8203;</span>

        <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-2xl sm:w-full">
          <div className="absolute top-0 right-0 pt-4 pr-4">
            <button
              type="button"
              className="text-gray-400 hover:text-gray-500"
              onClick={onClose}
            >
              <X className="h-6 w-6" />
            </button>
          </div>

          <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
            <div className="sm:flex sm:items-start">
              <div className="mt-3 text-center sm:mt-0 sm:text-left w-full">
                <h3 className="text-lg leading-6 font-medium text-gray-900">
                  {isEditMode ? 'Edit Plan' : 'Create New Plan'}
                </h3>
                
                <form onSubmit={handleSubmit} className="mt-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {/* Plan Name */}
                    <div className="col-span-2">
                      <label htmlFor="plan_name" className="block text-sm font-medium text-gray-700">
                        Plan Name <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        name="plan_name"
                        id="plan_name"
                        value={formData.plan_name || ''}
                        onChange={handleInputChange}
                        className={`mt-1 block w-full border ${
                          formErrors.plan_name ? 'border-red-300' : 'border-gray-300'
                        } rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-orange-500 focus:border-orange-500 sm:text-sm`}
                      />
                      {formErrors.plan_name && (
                        <p className="mt-1 text-sm text-red-600">{formErrors.plan_name}</p>
                      )}
                    </div>

                    {/* Description */}
                    <div className="col-span-2">
                      <label htmlFor="description" className="block text-sm font-medium text-gray-700">
                        Description <span className="text-red-500">*</span>
                      </label>
                      <textarea
                        name="description"
                        id="description"
                        rows={3}
                        value={formData.description || ''}
                        onChange={handleInputChange}
                        className={`mt-1 block w-full border ${
                          formErrors.description ? 'border-red-300' : 'border-gray-300'
                        } rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-orange-500 focus:border-orange-500 sm:text-sm`}
                      ></textarea>
                      {formErrors.description && (
                        <p className="mt-1 text-sm text-red-600">{formErrors.description}</p>
                      )}
                    </div>

                    {/* Pricing */}
                    <div>
                      <label htmlFor="monthly_price" className="block text-sm font-medium text-gray-700">
                        Monthly Price ($) <span className="text-red-500">*</span>
                      </label>
                      <div className="mt-1 relative rounded-md shadow-sm">
                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                          <span className="text-gray-500 sm:text-sm">$</span>
                        </div>
                        <input
                          type="number"
                          name="monthly_price"
                          id="monthly_price"
                          min="0"
                          step="0.01"
                          value={formData.monthly_price || ''}
                          onChange={handleInputChange}
                          className={`mt-1 block w-full border pl-7 ${
                            formErrors.monthly_price ? 'border-red-300' : 'border-gray-300'
                          } rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-orange-500 focus:border-orange-500 sm:text-sm`}
                        />
                      </div>
                      {formErrors.monthly_price && (
                        <p className="mt-1 text-sm text-red-600">{formErrors.monthly_price}</p>
                      )}
                    </div>

                    <div>
                      <label htmlFor="yearly_price" className="block text-sm font-medium text-gray-700">
                        Yearly Price ($) <span className="text-red-500">*</span>
                      </label>
                      <div className="mt-1 relative rounded-md shadow-sm">
                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                          <span className="text-gray-500 sm:text-sm">$</span>
                        </div>
                        <input
                          type="number"
                          name="yearly_price"
                          id="yearly_price"
                          min="0"
                          step="0.01"
                          value={formData.yearly_price || ''}
                          onChange={handleInputChange}
                          className={`mt-1 block w-full border pl-7 ${
                            formErrors.yearly_price ? 'border-red-300' : 'border-gray-300'
                          } rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-orange-500 focus:border-orange-500 sm:text-sm`}
                        />
                      </div>
                      {formErrors.yearly_price && (
                        <p className="mt-1 text-sm text-red-600">{formErrors.yearly_price}</p>
                      )}
                    </div>

                    {/* Memory Limits */}
                    <div>
                      <label htmlFor="memory_limit" className="block text-sm font-medium text-gray-700">
                        Storage Limit <span className="text-red-500">*</span>
                      </label>
                      <div className="mt-1 flex rounded-md shadow-sm">
                        <input
                          type="number"
                          name="memory_limit"
                          id="memory_limit"
                          min="1"
                          value={formData.memory_limit || ''}
                          onChange={handleInputChange}
                          className={`block w-full rounded-none rounded-l-md border ${
                            formErrors.memory_limit ? 'border-red-300' : 'border-gray-300'
                          } py-2 px-3 focus:outline-none focus:ring-orange-500 focus:border-orange-500 sm:text-sm`}
                        />
                        <select
                          name="memory_unit"
                          id="memory_unit"
                          value={formData.memory_unit || 'GB'}
                          onChange={handleInputChange}
                          className={`inline-flex items-center rounded-r-md border ${
                            formErrors.memory_unit ? 'border-red-300' : 'border-gray-300'
                          } bg-gray-50 px-3 text-sm text-gray-500 focus:outline-none focus:ring-orange-500 focus:border-orange-500`}
                        >
                          <option value="MB">MB</option>
                          <option value="GB">GB</option>
                          <option value="TB">TB</option>
                        </select>
                      </div>
                      {formErrors.memory_limit && (
                        <p className="mt-1 text-sm text-red-600">{formErrors.memory_limit}</p>
                      )}
                      {formErrors.memory_unit && (
                        <p className="mt-1 text-sm text-red-600">{formErrors.memory_unit}</p>
                      )}
                    </div>

                    <div>
                      <label htmlFor="recipient_limit" className="block text-sm font-medium text-gray-700">
                        Recipient Limit <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="number"
                        name="recipient_limit"
                        id="recipient_limit"
                        min="1"
                        value={formData.recipient_limit || ''}
                        onChange={handleInputChange}
                        className={`mt-1 block w-full border ${
                          formErrors.recipient_limit ? 'border-red-300' : 'border-gray-300'
                        } rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-orange-500 focus:border-orange-500 sm:text-sm`}
                      />
                      {formErrors.recipient_limit && (
                        <p className="mt-1 text-sm text-red-600">{formErrors.recipient_limit}</p>
                      )}
                    </div>

                    
                  </div>

                 
                </form>
              </div>
            </div>
          </div>
          
          <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
            <button
              type="button"
              className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-orange-600 text-base font-medium text-white hover:bg-orange-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-orange-500 sm:ml-3 sm:w-auto sm:text-sm"
              onClick={handleSubmit}
            >
              {isEditMode ? 'Update Plan' : 'Create Plan'}
            </button>
            <button
              type="button"
              className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
              onClick={onClose}
            >
              Cancel
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PlanFormModal;